'use client';

import { loadDIModules } from '@actr/di/src/hoc/load-di-modules';
import { useInject } from '@actr/di/src/hook/use-inject';
import { aviaSearchFormDirections } from '@actr/specs/src/avia/search-form-directions';
import aviaSearchDIModule from '@actr/stores/src/modules/avia/search';
import seoReviewsDIModule from '@actr/stores/src/modules/seo/reviews';
import { AviaSearchFormStore } from '@actr/stores/src/stores/stores/avia/search-form';
import { Sizes } from '@actr/style/src/specs/theme-v2';
import { Space } from '@actr/ui/src/components/_atoms/space';
import { Container } from '@actr/ui/src/components/container';
import { ContentBlock } from '@actr/ui/src/components/content-block';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { FC, PropsWithChildren, useEffect } from 'react';

import CountryToCityPopularDirections from '@/components/avia/country-to-city/popular-directions';
import Recommendations from '@/components/avia/recommendations';
import SearchFormHeader from '@/components/avia/search-form-header';
import Breadcrumb from '@/components/common/breadcrumb';
import ServiceAdvantages from '@/components/page-constucts/advantages';
import Faq from '@/components/page-constucts/faq';
import InnerHtmlTextBlock from '@/components/page-constucts/inner-html-text-block';
import ReviewsBlock from '@/components/page-constucts/reviews-block';
import SideNavBar from '@/components/page-constucts/side-navigation-bar';
import WhereToFly from '@/components/page-constucts/where-to-fly';
import { SeoCountryToCity } from '@/services/models/avia/country-to-city';
import { getContentBlocks } from '@/utils/get-content-blocks';
import getNavBarItems from '@/utils/nav-bar';

import * as Styled from './style.css';

export type BaseCountryToCityPageProps = {
  /** контент страницы */
  data: SeoCountryToCity;
  /** путь для хлебной крошки - страна текущего города */
  pathFrom: string;
  /** путь для хлебной крошки - текущий город */
  pathIn: string;
}

const BaseCountryToCityPage: FC <BaseCountryToCityPageProps & PropsWithChildren> = observer(({ data, children, pathIn, pathFrom }) => {
  const { search_form_title, breadcrumbs, content, location, destination_country } = data;
  const { info, where_to_fly, faq, feedback,
    advantages, app_banner, to_city_recommendations,
    to_city_popular_routes } = content || {};
  const aviaSearchForm = useInject(AviaSearchFormStore);
  const itemsForNavBar = getNavBarItems(content);

  useEffect(() => {
    aviaSearchForm.stopStore();

    if (content && to_city_recommendations?.list) {
      const seoSegmentTo = {
        iata: to_city_recommendations.list[0].arr.city.code,
        name: to_city_recommendations.list[0].arr.city.title,
        airport_aggregation_iata: to_city_recommendations.list[0].arr.city.code,
        country: {
          name: to_city_recommendations.list[0].arr.city.title,
          iata: to_city_recommendations.list[0].arr.city.code,
        },
        city: {
          name: to_city_recommendations.list[0].arr.city.title,
          iata: to_city_recommendations.list[0].arr.city.code,
        },
      };

      const segments = [{ ...aviaSearchForm.defaultSegment, to: seoSegmentTo }];
      runInAction(() => {
        aviaSearchForm.setSegments(segments);
        aviaSearchForm.setDirection(aviaSearchFormDirections.TWOWAY);
      });
    }
  }, [aviaSearchForm, content, to_city_recommendations]);

  const crumbs = [{
    title: 'Дешевые авиабилеты',
    path: '/',
  }, {
    path: '/countries',
    title: 'Страны',
  }, {
    path: `/countries/${pathFrom}`,
    title: location?.country?.name || 'Страна',
  }, {
    path: `/countries/${pathIn}`,
    title: breadcrumbs,
  }];

  const contentBlocks = content ? [
    {
      key: 'info',
      condition: !!info?.description,
      render: () => info ? (
        <ContentBlock id="city_information">
          <InnerHtmlTextBlock
            html={info.description}
          />
        </ContentBlock>
      ) : null
    },
    {
      key: 'to_city_recommendations',
      condition: !!to_city_recommendations && !!to_city_recommendations.list,
      render: () => to_city_recommendations ? (
        <Recommendations id="to_city_recommendations" recommendations={to_city_recommendations} />
      ) : null
    },
    {
      key: 'to_city_popular_routes',
      condition: !!to_city_popular_routes && !!to_city_popular_routes.list,
      render: () => to_city_popular_routes ? (
        <CountryToCityPopularDirections
          title={to_city_popular_routes.title}
          list={to_city_popular_routes.list}
        />
      ) : null
    },
    {
      key: 'where_to_fly',
      condition: !!where_to_fly && !!where_to_fly.list,
      render: () => where_to_fly ? (
        <WhereToFly
          title={where_to_fly.title}
          subtitle={where_to_fly.subtitle}
          cities={where_to_fly.list.cities}
          countries={where_to_fly.list.countries}
          directions={where_to_fly.list.directions}
          allCitiesLink={destination_country}
        />
      ) : null
    },
    {
      key: 'faq',
      condition: !!faq?.list,
      render: () => faq ? (
        <Faq list={faq?.list} title={faq.title} />
      ) : null
    },
    {
      key: 'feedback',
      condition: !!feedback?.list,
      render: () => feedback ? (
        <ReviewsBlock title={feedback.title} feedback={feedback?.list} />
      ) : null
    },
    {
      key: 'advantages',
      condition: !!advantages?.list?.enable,
      render: () => (<ServiceAdvantages />)
    }
  ] : [];

  const blocks = getContentBlocks({
    content, contentBlocks
  });

  return (
    <>
      <SearchFormHeader
        title={search_form_title || ''}
      />
      <Container>
        <Breadcrumb crumb={crumbs} dynamic={false} />
        <Space size={Sizes.M} />
        <Container className={Styled.ColumnWrapper}>
          <SideNavBar items={itemsForNavBar} isAppBannerEnable={!!app_banner?.list?.enable} isHideNavBarOnMobile={true} />
          <div className={Styled.MainInfo}>
            {blocks.map(block => (
              <React.Fragment key={block.key}>
                {block.render()}
              </React.Fragment>
            ))}
          </div>
        </Container>
        {children}
      </Container>
    </>
  );
});

export default loadDIModules(BaseCountryToCityPage, aviaSearchDIModule, seoReviewsDIModule);
